import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api, { getClients } from '../services/api';

const DOC_TYPES = [
  { key:'civil_id',            label:'Civil ID' },
  { key:'business_licence',    label:'Business Licence' },
  { key:'commercial_register', label:'Commercial Register' },
  { key:'contract',            label:'Contract' },
  { key:'other',               label:'Other Attachment' },
];

function printContract(c) {
  const win = window.open('', '_blank');
  const docs = DOC_TYPES.map(t => `
    <tr>
      <td style="font-weight:600">${t.label}</td>
      <td>${c[t.key+'_name'] ? '<span style="color:#166534;font-weight:600">✓ Uploaded</span>' : '<span style="color:#dc2626">✗ Missing</span>'}</td>
      <td style="color:#666;font-size:12px">${c[t.key+'_name']||'--'}</td>
    </tr>
  `).join('');
  win.document.write(`<!DOCTYPE html><html><head><title>Contract - ${c.client?.name}</title>
  <style>
    body{font-family:Arial,sans-serif;padding:30px;color:#111}
    .header{display:flex;justify-content:space-between;border-bottom:2px solid #00b368;padding-bottom:16px;margin-bottom:24px}
    .company{font-size:22px;font-weight:700;color:#00b368}
    .client-box{background:#f0fdf4;border:1px solid #86efac;border-radius:8px;padding:16px;margin-bottom:20px}
    table{width:100%;border-collapse:collapse;font-size:13px}
    th{background:#f5f5f5;padding:10px 12px;text-align:left;font-size:11px;text-transform:uppercase;border-bottom:2px solid #eee;color:#666}
    td{padding:12px 12px;border-bottom:1px solid #f0f0f0}
    .footer{margin-top:30px;text-align:center;font-size:11px;color:#aaa;border-top:1px solid #eee;padding-top:12px}
  </style></head><body><img src="https://teknulugy.online/logo.svg" style="height:50px;display:block;margin:0 auto 10px auto">
  <div class="header">
    <div><div class="company">TEKNULUGY</div><div style="font-size:12px;color:#666">IT Services - Kuwait</div></div>
    <div style="text-align:right">
      <div style="font-size:18px;font-weight:700">Client Contract Summary</div>
      <div style="font-size:12px;color:#666">Generated: ${new Date().toLocaleDateString()}</div>
    </div>
  </div>
  <div class="client-box">
    <div style="font-size:11px;color:#166534;font-weight:700;text-transform:uppercase;margin-bottom:4px">Client</div>
    <div style="font-size:20px;font-weight:700">${c.client?.name||'--'}</div>
    ${c.notes?'<div style="font-size:13px;color:#666;margin-top:6px">Notes: '+c.notes+'</div>':''}
  </div>
  <table>
    <thead><tr><th>Document Type</th><th>Status</th><th>Filename</th></tr></thead>
    <tbody>${docs}</tbody>
  </table>
  <div class="footer">Teknulugy IT Services - Ahmed Al Jaber Street, Baitak Tower, Floor 1, Office D - +965 55107000</div>
  </body></html>`);
  win.document.close();
  setTimeout(()=>win.print(), 500);
}

export default function ContractsPage() {
  const qc = useQueryClient();
  const [showForm, setShowForm]       = useState(false);
  const [editing, setEditing]         = useState(null);
  const [selectedClient, setSelectedClient] = useState('');
  const [files, setFiles]             = useState({});
  const [notes, setNotes]             = useState('');
  const [search, setSearch]           = useState('');

  const { data:contractsData, isLoading } = useQuery({
    queryKey:['contracts'],
    queryFn:()=>api.get('/contracts').then(r=>r.data),
  });
  const { data:clientData } = useQuery({
    queryKey:['clients-list'],
    queryFn:()=>getClients({limit:200}).then(r=>r.data),
  });

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!selectedClient) throw new Error('Please select a client');
      const payload = { client_id:selectedClient, notes };
      const readFile = file => new Promise(resolve => {
        const r = new FileReader();
        r.onload = e => resolve(e.target.result);
        r.readAsDataURL(file);
      });
      for (const type of DOC_TYPES) {
        if (files[type.key]) {
          payload[type.key+'_file']     = await readFile(files[type.key]);
          payload[type.key+'_filename'] = files[type.key].name;
        }
      }
      return api.post('/contracts', payload);
    },
    onSuccess:()=>{ qc.invalidateQueries(['contracts']); setShowForm(false); setEditing(null); setFiles({}); setNotes(''); setSelectedClient(''); toast.success('Contract saved!'); },
    onError: e=>toast.error(e.message||e.response?.data?.message||'Error'),
  });

  const deleteMut = useMutation({
    mutationFn: id=>api.delete('/contracts/'+id),
    onSuccess:()=>{ qc.invalidateQueries(['contracts']); toast.success('Deleted'); },
  });

  const downloadFile = async (id, type, name) => {
    try {
      const res = await api.get('/contracts/'+id+'/download/'+type, { responseType:'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href=url; a.download=name||type; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('File not found'); }
  };

  const openEdit = (c) => {
    setEditing(c);
    setSelectedClient(String(c.client_id));
    setNotes(c.notes||'');
    setFiles({});
    setShowForm(false);
  };

  const contracts = (contractsData?.data||[]).filter(c=>
    !search || c.client?.name?.toLowerCase().includes(search.toLowerCase())
  );
  const clients = clientData?.data || [];

  const formTitle = editing ? 'Edit Contract — '+editing.client?.name : 'New Client Contract';

  return (
    <div>
      <div className="g3" style={{marginBottom:20}}>
        <div className="card-sm"><div className="stat-label">Total Contracts</div><div className="stat-val">{contractsData?.data?.length||0}</div></div>
        <div className="card-sm"><div className="stat-label">With Contract</div><div className="stat-val" style={{color:'var(--green)'}}>{(contractsData?.data||[]).filter(c=>c.contract_path).length}</div></div>
        <div className="card-sm"><div className="stat-label">Pending Docs</div><div className="stat-val" style={{color:'var(--warn)'}}>{(contractsData?.data||[]).filter(c=>!c.contract_path).length}</div></div>
      </div>

      <div style={{display:'flex',gap:10,marginBottom:16,alignItems:'center'}}>
        <input className="search-input" placeholder="Search by client..." value={search} onChange={e=>setSearch(e.target.value)}/>
        <button className="btn btn-primary" style={{marginLeft:'auto'}} onClick={()=>{setShowForm(v=>!v);setEditing(null);setSelectedClient('');setNotes('');setFiles({});}}>+ Add Contract</button>
      </div>

      {(showForm||editing) && (
        <div className="card" style={{marginBottom:16}}>
          <div className="form-section-title">{formTitle}</div>
          <div className="form-grid" style={{marginBottom:20}}>
            <div className="form-group"><label className="form-label">Client *</label>
              <select className="form-select" value={selectedClient} onChange={e=>setSelectedClient(e.target.value)} disabled={!!editing}>
                <option value="">Select client...</option>
                {clients.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </select></div>
            <div className="form-group"><label className="form-label">Notes</label>
              <input className="form-input" value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Notes..."/></div>
          </div>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:20}}>
            {DOC_TYPES.map(type=>(
              <div key={type.key} style={{background:'var(--surface2)',border:'1.5px solid var(--border)',borderRadius:10,padding:14}}>
                <div style={{fontSize:13,fontWeight:700,color:'var(--text)',marginBottom:8}}>{type.label}</div>
                {editing && editing[type.key+'_name'] && !files[type.key] && (
                  <div style={{fontSize:11,color:'var(--green)',marginBottom:6,fontWeight:600}}>
                    ✓ Current: {editing[type.key+'_name']}
                    <button className="btn btn-outline btn-sm" style={{marginLeft:8,fontSize:10}} onClick={()=>downloadFile(editing.id,type.key,editing[type.key+'_name'])}>Download</button>
                  </div>
                )}
                <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={e=>setFiles(p=>({...p,[type.key]:e.target.files[0]}))}
                  style={{width:'100%',fontSize:12,color:'var(--muted)'}}/>
                {files[type.key]&&<div style={{fontSize:11,color:'var(--green)',marginTop:6,fontWeight:600}}>✓ {files[type.key].name}</div>}
              </div>
            ))}
          </div>

          <div style={{display:'flex',gap:8}}>
            <button className="btn btn-primary" onClick={()=>saveMut.mutate()} disabled={saveMut.isPending}>
              {saveMut.isPending?'Saving...':'Save Contract'}
            </button>
            <button className="btn btn-outline" onClick={()=>{setShowForm(false);setEditing(null);setFiles({});setSelectedClient('');setNotes('');}}>Cancel</button>
          </div>
        </div>
      )}

      <div className="card">
        {isLoading?<div className="loading-center"><div className="spinner"/></div>:(
          <table className="tbl">
            <thead>
              <tr><th>Client</th><th>Civil ID</th><th>Business Lic.</th><th>Comm. Register</th><th>Contract</th><th>Other</th><th>Notes</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {contracts.length===0&&<tr><td colSpan={8} className="empty-state">No contracts found</td></tr>}
              {contracts.map(c=>(
                <tr key={c.id}>
                  <td style={{fontWeight:700}}>{c.client?.name||'--'}</td>
                  {DOC_TYPES.map(type=>(
                    <td key={type.key}>
                      {c[type.key+'_path']
                        ? <button className="btn btn-outline btn-sm" style={{color:'var(--green)',borderColor:'var(--green)'}} onClick={()=>downloadFile(c.id,type.key,c[type.key+'_name'])}>
                            Download
                          </button>
                        : <span style={{color:'#ccc',fontSize:12}}>--</span>
                      }
                    </td>
                  ))}
                  <td style={{color:'var(--muted)',fontSize:12,maxWidth:100,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.notes||'--'}</td>
                  <td>
                    <div style={{display:'flex',gap:6}}>
                      <button className="btn btn-outline btn-sm" onClick={()=>openEdit(c)}>Edit</button>
                      <button className="btn btn-outline btn-sm" style={{color:'var(--info)',borderColor:'var(--info)'}} onClick={()=>printContract(c)}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
                        Print
                      </button>
                      <button className="btn btn-danger btn-sm" onClick={()=>{if(window.confirm('Delete contract?'))deleteMut.mutate(c.id);}}>X</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
